import { domainProvider } from '@mathflat/shared/@common/domain/domainProvider'
import type { AxiosError, AxiosResponse } from 'axios'
import axios from 'axios'

import { mpEventInfo } from '@/constant'
import type { WorksheetExportType } from '@/contentComponents/modal/Download/type'
import { localStorageService } from '@/service/storage.service'
import type { PrintDefault, PrintError } from '@/types/common/api'
import {
  wrapErrorHandlerWithJwt,
  wrapRequestInterceptorWithJwt,
} from '@/utils/axios/jwtInterceptors'
import { envUtils } from '@/utils/envUtils'
import { _mixpanelUtils } from '@/utils/trackings/_mixpanel'

const makeRequestURL = (url: string) => {
  const dev = `https://print-dev.${domainProvider.TARGET_DOMAIN}/v1.0`
  const staging = `https://print-staging.${domainProvider.TARGET_DOMAIN}/v1.0`
  const live = `https://print.${domainProvider.TARGET_DOMAIN}/v1.0`

  const localPrintApi = localStorageService.getItem('print-api')

  if (localPrintApi) {
    return localPrintApi + url
  }
  if (envUtils.isStaging || localStorage.getItem('DEV_API') === 'stg') {
    return staging + url
  }
  if (envUtils.isLive) {
    return live + url
  }

  return dev + url
}

export const printAxios = axios.create()

printAxios.interceptors.request.use(wrapRequestInterceptorWithJwt((config) => config))

printAxios.interceptors.response.use(
  (response: AxiosResponse) => {
    _mixpanelUtils.printApiTrack(response)
    return response
  },
  wrapErrorHandlerWithJwt(
    async (error: unknown) => {
      const axiosError = error as AxiosError<PrintError>
      _mixpanelUtils.printErrorTrack(axiosError)
      return Promise.reject(axiosError)
    },
    async (originalRequest, newAccessToken) => {
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
      return printAxios(originalRequest)
    },
  ),
)

const printApi = {
  getPrintList: (teacherId: string) => {
    return printAxios.get<PrintDefault<res.PrintList>>(makeRequestURL(`/agents/${teacherId}`), {
      params: { mp_event_name: mpEventInfo.PC버전등록_인쇄 },
    })
  },
  postMQTTPrintWorksheet: (data: req.PrintWorksheet, type?: WorksheetExportType) => {
    return printAxios.post<res.PrintResponse>(makeRequestURL(`/print/mqtt/piece`), data, {
      params: {
        ...(type !== 'THEME_ARITHMETIC' && { mp_event_name: mpEventInfo['학습지_인쇄(비동기)'] }),
      },
    })
  },
  postMQTTPrintSchoolTest: (
    data: req.CommonPrintOption & {
      tests: {
        id: number
        page: string
      }[]
    },
  ) => {
    return printAxios.post<res.PrintResponse>(makeRequestURL('/print/mqtt/school-tests'), data)
  },
  postMQTTPrintCalculation: (data: req.PrintCalculation) => {
    return printAxios.post<res.PrintResponse>(makeRequestURL(`/print/mqtt/piece`), data, {
      params: { mp_event_name: mpEventInfo['연산_인쇄(비동기)'] },
    })
  },
  postMQTTPrintCustomWorkbook: (data: req.PrintCustomWorkbook) => {
    return printAxios.post<res.PrintResponse>(makeRequestURL(`/print/mqtt/custombook`), data, {
      params: { mp_event_name: mpEventInfo['학원교재_인쇄(비동기)'] },
    })
  },
  postPrintCustomWorkbookQuick: (data: req.PrintCustomWorkbook) => {
    return printAxios.post<res.PrintResponse>(makeRequestURL(`/print/custombook-quick`), data, {
      params: { mp_event_name: mpEventInfo.교재빠른정답 },
    })
  },
  postMQTTPrintReport: (data: req.PrintReport) => {
    return printAxios.post<res.PrintResponse>(makeRequestURL(`/print/mqtt/report`), data, {
      params: { mp_event_name: mpEventInfo.보고서_인쇄 },
    })
  },
  postMQTTPrintReportRealTime: (data: req.PrintReportRealTime) => {
    return printAxios.post<res.PrintResponse>(makeRequestURL(`/print/mqtt/report-realtime`), data)
  },
  postPrintOneClickReport: (data: req.PrintOneClickReport) => {
    return printAxios.post<res.PrintResponse>(makeRequestURL(`/print/report-one`), data, {
      params: { mp_event_name: mpEventInfo['원클릭보고서_인쇄(비동기)'] },
    })
  },
  postPrintMQTTReportCas: (data: req.PrintCasOneClickReport) => {
    return printAxios.post<res.PrintResponse>(makeRequestURL(`/print/mqtt/report-cas`), data, {
      params: { mp_event_name: mpEventInfo['원클릭보고서_인쇄(비동기)'] },
    })
  },
}

export default printApi
